import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Header,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Search } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Input } from './input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './pagination';
import { Skeleton } from './skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    /** Applied to the header cell, and to a wrapping div around the body
     * cell's content, for non-sizing concerns (e.g. text alignment). If the
     * cell's own content can overflow its column width, give the actual
     * text-bearing element (not this wrapper) `truncate`, and if that
     * element sits inside a flex row, give the row `min-w-0` too -
     * otherwise the flex item won't shrink below its content size and no
     * ellipsis will show. */
    className?: string;
    /** Real fixed width in px for this column, applied to both the header
     * and body cells. As soon as any column in a table declares `width` (or
     * `widthPercent`), that table renders with `table-layout: fixed` AND
     * stops being forced to fill its container (`w-full` is dropped) - it
     * sizes to the sum of its declared column widths instead, so a column
     * with no explicit `width` never has to stretch to absorb whatever
     * space is left over. Give every column an explicit `width` for a
     * fully deterministic, compact table; a column with only `minWidth`
     * still works but its exact resolved width in that case is
     * browser-dependent. Tables where no column declares `width` (or
     * `widthPercent`) keep the original content-fit, full-width
     * `table-layout: auto` behavior. */
    width?: number;
    /** Floor width in px. Use on a column with no `width` to stop it being
     * crushed on a narrow viewport, or alongside `width` for the same
     * lower bound if the table is ever squeezed. */
    minWidth?: number;
    /** Column width as a percentage (0-100) instead of a fixed pixel width -
     * use when the table SHOULD still fill its container (unlike the
     * fixed-px case above), but every column should grow/shrink together
     * in proportion rather than one flexible column absorbing 100% of
     * whatever space is left over. Give every column in the table a
     * `widthPercent` summing to ~100 for a stable, proportioned full-width
     * layout regardless of container size. Takes precedence over `width`
     * if both are set on the same column; still triggers `table-layout:
     * fixed`, but does NOT drop `w-full` the way plain `width` does. */
    widthPercent?: number;
  }
}

function cellSizeStyle(meta?: {
  width?: number;
  minWidth?: number;
  widthPercent?: number;
}): React.CSSProperties | undefined {
  if (meta?.widthPercent !== undefined) {
    return { width: `${meta.widthPercent}%` };
  }
  if (meta?.width === undefined && meta?.minWidth === undefined) return undefined;
  return { width: meta.width, minWidth: meta.minWidth ?? meta.width };
}

function SortableHeader<TData>({ header }: { header: Header<TData, unknown> }) {
  if (!header.column.getCanSort()) {
    return flexRender(header.column.columnDef.header, header.getContext());
  }
  const sorted = header.column.getIsSorted();
  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      className="-mx-2 h-auto p-0 px-2 py-1 font-medium"
      onClick={header.column.getToggleSortingHandler()}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
      {sorted === 'asc' ? (
        <ArrowUp className="h-3.5 w-3.5" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="h-3.5 w-3.5" />
      ) : (
        <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </Button>
  );
}

/** Manual-sort variant of SortableHeader for a server-paginated table: a
 * click reports the column id to the caller (triggering a refetch with a new
 * order) instead of asking TanStack to re-sort the current page's rows in
 * place, which would silently only reorder what's already been fetched. */
function ManualSortableHeader<TData>({
  header,
  manualSort,
}: {
  header: Header<TData, unknown>;
  manualSort: DataTableManualSort;
}) {
  const columnId = header.column.id;
  if (!(columnId in manualSort.sortableColumns)) {
    return flexRender(header.column.columnDef.header, header.getContext());
  }
  const sorted = manualSort.sortBy === columnId ? manualSort.sortDir : false;
  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      className="-mx-2 h-auto p-0 px-2 py-1 font-medium"
      onClick={() => manualSort.onSortChange(columnId)}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
      {sorted === 'asc' ? (
        <ArrowUp className="h-3.5 w-3.5" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="h-3.5 w-3.5" />
      ) : (
        <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </Button>
  );
}

export interface DataTableManualSort {
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSortChange: (column: string) => void;
  /** Column ids eligible for manual sort, mapped to a label (unused by the
   * header itself today, but keeps the map self-documenting at call sites). */
  sortableColumns: Record<string, string>;
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  searchKey?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  filterSlot?: React.ReactNode;
  /** Rendered in place of the default "No results." cell when there's no
   * data - the search bar/filterSlot row still renders above it either
   * way, so a caller filtering to zero rows doesn't lose its own filter
   * controls along with the table body. */
  emptyState?: React.ReactNode;
  pageSize?: number;
  isLoading?: boolean;
  /** Renders clickable, sortable column headers (client-side sort via
   * TanStack's getSortedRowModel, already wired up regardless of this flag).
   * Off by default so existing server-side-paginated tables keep their
   * current header rendering - turn on only for tables where the full
   * dataset is present client-side, since a header click here would
   * otherwise misleadingly only sort the currently-fetched page. */
  enableSorting?: boolean;
  /** Sort applied on first render, before any header click - e.g.
   * `[{ id: 'name', desc: false }]`. Only meaningful alongside
   * `enableSorting`; has no effect afterward (it seeds initial state, it
   * doesn't control sorting). */
  initialSorting?: SortingState;
  /** Sortable headers for a server-paginated table: a click reports the
   * column to the caller (to refetch in a new order) rather than resorting
   * the current page in place. Mutually exclusive with enableSorting in
   * practice — use this one whenever totalCount/onPageChange are set. */
  manualSort?: DataTableManualSort;
  // Server-side mode: when provided, disables internal filtering/pagination
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  totalCount?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (row: TData) => void;
  /** When set, renders every row (no pagination) inside a vertically
   * scrollable container capped at this CSS height, with the header row
   * pinned via sticky positioning - use for a short, fixed-height list
   * where scrolling reads better than paging. Ignored in server-side mode
   * (totalCount/onPageChange) and takes precedence over pageSize. */
  scrollHeight?: string;
  /** When provided together with onRowSelectionChange, renders a leading
   * checkbox column: a per-row checkbox plus a header "select all on this
   * page" checkbox (page-scoped - it never reaches into other pages).
   * Selection state itself is NOT owned by DataTable - the caller owns it
   * (e.g. as a Map/Set keyed by id that must survive `data` being replaced
   * wholesale by pagination). Requires `getRowId` for the ids to be stable
   * across pages/refetches. */
  enableRowSelection?: boolean;
  getRowId?: (row: TData) => string;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (rowSelection: Record<string, boolean>) => void;
  /** Per-row override disabling that row's checkbox, with a tooltip reason.
   * Only consulted when enableRowSelection is set. */
  isRowSelectable?: (row: TData) => { selectable: boolean; reason?: string };
}

function ServerPagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('ellipsis');
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <span className="text-sm text-muted-foreground tabular-nums px-2">
            Page {currentPage} of {totalPages}
          </span>
        </PaginationItem>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          />
        </PaginationItem>
        {pages.map((p, i) => {
          const key = p === 'ellipsis' ? `ellipsis-${i}` : p;
          return p === 'ellipsis' ? (
            <PaginationItem key={key}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={key}>
              <PaginationLink isActive={p === currentPage} onClick={() => onPageChange(p)}>
                {p}
              </PaginationLink>
            </PaginationItem>
          );
        })}
        <PaginationItem>
          <PaginationNext
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

function renderRows<TData>({
  isLoading,
  rows,
  columns,
  onRowClick,
  emptyState,
}: {
  isLoading?: boolean;
  rows: Row<TData>[];
  columns: ColumnDef<TData>[];
  onRowClick?: (row: TData) => void;
  emptyState?: React.ReactNode;
}) {
  if (isLoading) {
    return Array.from({ length: 5 }, (_, i) => (
      <TableRow key={`skeleton-row-${i}`}>
        {columns.map((_col, j) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows are static placeholders
          <TableCell key={`skeleton-cell-${j}`}>
            <Skeleton className="h-5 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ));
  }
  if (rows.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
          {emptyState ?? 'No results.'}
        </TableCell>
      </TableRow>
    );
  }
  return rows.map((row) => (
    <TableRow
      key={row.id}
      data-state={row.getIsSelected() && 'selected'}
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
      className={onRowClick ? 'cursor-pointer' : undefined}
    >
      {row.getVisibleCells().map((cell) => {
        const meta = cell.column.columnDef.meta;
        return (
          <TableCell key={cell.id} style={cellSizeStyle(meta)}>
            {meta?.className ? (
              <div className={meta.className}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        );
      })}
    </TableRow>
  ));
}

export function DataTable<TData>({
  columns,
  data,
  searchKey,
  showSearch,
  searchPlaceholder = 'Search',
  filterSlot,
  emptyState,
  pageSize = 20,
  isLoading,
  enableSorting = false,
  initialSorting,
  manualSort,
  searchValue,
  onSearchChange,
  totalCount,
  currentPage = 1,
  onPageChange,
  onRowClick,
  scrollHeight,
  enableRowSelection = false,
  getRowId,
  rowSelection,
  onRowSelectionChange,
  isRowSelectable,
}: DataTableProps<TData>) {
  const isServerSide = totalCount !== undefined && onPageChange !== undefined;
  const isScrollable = !isServerSide && !!scrollHeight;
  const displaySearch = showSearch ?? !!searchKey;
  const hasFixedWidthColumn = columns.some(
    (col) => col.meta?.width !== undefined || col.meta?.widthPercent !== undefined,
  );
  // Only a plain px `width` drops the forced w-full (see ColumnMeta's doc
  // comment) - `widthPercent` is meant to keep the table full-width while
  // still giving every column a stable, proportional share of it.
  const hasPxWidthColumn = columns.some((col) => col.meta?.width !== undefined);

  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [internalPage, setInternalPage] = useState(0);

  // In scroll mode every row renders in one page, so the built-in
  // pagination controls never appear (data.length > effectivePageSize is
  // always false) and the caller relies on the scroll container instead.
  const effectivePageSize = isScrollable ? Math.max(data.length, 1) : pageSize;

  const selectionColumn: ColumnDef<TData> = {
    id: 'select',
    meta: { width: 40 },
    // No "select all" header checkbox: toggleAllPageRowsSelected sets
    // TanStack's row-selection state directly, bypassing isRowSelectable
    // entirely - a caller capping selection size (e.g. max 5) would have
    // "select all" silently select every row on the page and only fail at
    // submit time, defeating the whole point of per-row eligibility gating.
    header: () => null,
    cell: ({ row }) => {
      const gate = isRowSelectable?.(row.original);
      const disabled = !!gate && !gate.selectable;
      const checkbox = (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          aria-label="Select row"
        />
      );
      if (disabled && gate?.reason) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{checkbox}</span>
              </TooltipTrigger>
              <TooltipContent>{gate.reason}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      return checkbox;
    },
  };
  const tableColumns = enableRowSelection ? [selectionColumn, ...columns] : columns;

  const table = useReactTable({
    data,
    columns: tableColumns,
    getRowId,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: isServerSide ? undefined : getFilteredRowModel(),
    getPaginationRowModel: isServerSide ? undefined : getPaginationRowModel(),
    enableSorting,
    onSortingChange: setSorting,
    onColumnFiltersChange: isServerSide ? undefined : setColumnFilters,
    manualPagination: isServerSide,
    manualFiltering: isServerSide,
    state: {
      sorting,
      columnFilters: isServerSide ? [] : columnFilters,
      pagination: isServerSide
        ? undefined
        : { pageIndex: internalPage, pageSize: effectivePageSize },
      rowSelection: rowSelection ?? {},
    },
    onRowSelectionChange: onRowSelectionChange
      ? (updater) => {
          const current = rowSelection ?? {};
          const next = typeof updater === 'function' ? updater(current) : updater;
          onRowSelectionChange(next);
        }
      : undefined,
    onPaginationChange: isServerSide
      ? undefined
      : (updater) => {
          if (typeof updater === 'function') {
            const next = updater({ pageIndex: internalPage, pageSize: effectivePageSize });
            setInternalPage(next.pageIndex);
          }
        },
  });

  const totalPages = isServerSide ? Math.ceil((totalCount ?? 0) / pageSize) : table.getPageCount();
  const showClientPagination = !isServerSide && data.length > effectivePageSize;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (onSearchChange) {
      onSearchChange(val);
    } else if (searchKey) {
      table.getColumn(searchKey)?.setFilterValue(val);
    }
  };

  const currentSearchValue = isServerSide
    ? (searchValue ?? '')
    : ((table.getColumn(searchKey ?? '')?.getFilterValue() as string) ?? '');

  const resultCount = isServerSide ? (totalCount ?? 0) : table.getFilteredRowModel().rows.length;

  return (
    <div className="space-y-3">
      {(displaySearch || filterSlot) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {displaySearch && (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder={searchPlaceholder}
                  value={currentSearchValue}
                  onChange={handleSearchChange}
                  className="pl-8 w-64"
                />
              </div>
            )}
            <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
              {resultCount} result{resultCount !== 1 ? 's' : ''}
            </span>
          </div>
          {filterSlot && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">{filterSlot}</div>
          )}
        </div>
      )}

      <div
        className={cn(
          'rounded-md border border-border',
          isScrollable ? 'overflow-y-auto' : 'overflow-hidden',
        )}
        style={isScrollable ? { maxHeight: scrollHeight } : undefined}
      >
        <Table
          className={hasPxWidthColumn ? 'w-auto' : undefined}
          style={hasFixedWidthColumn ? { tableLayout: 'fixed' } : undefined}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className={cn(
                  'bg-muted/30 hover:bg-muted/30',
                  isScrollable && 'sticky top-0 z-10 bg-muted hover:bg-muted',
                )}
              >
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  const ariaSort = manualSort
                    ? columnId in manualSort.sortableColumns
                      ? manualSort.sortBy === columnId
                        ? manualSort.sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                      : undefined
                    : header.column.getCanSort()
                      ? header.column.getIsSorted() === 'asc'
                        ? 'ascending'
                        : header.column.getIsSorted() === 'desc'
                          ? 'descending'
                          : 'none'
                      : undefined;
                  return (
                    <TableHead
                      key={header.id}
                      className={header.column.columnDef.meta?.className}
                      style={cellSizeStyle(header.column.columnDef.meta)}
                      aria-sort={ariaSort}
                    >
                      {header.isPlaceholder ? null : manualSort ? (
                        <ManualSortableHeader header={header} manualSort={manualSort} />
                      ) : (
                        <SortableHeader header={header} />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {renderRows({
              isLoading,
              rows: table.getRowModel().rows,
              columns: tableColumns,
              onRowClick,
              emptyState,
            })}
          </TableBody>
        </Table>
      </div>

      {isServerSide && onPageChange && (
        <div className="flex justify-center">
          <ServerPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}

      {showClientPagination && (
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <span>
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
